import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, Search } from "lucide-react";
import { listAffiliates } from "@/api/commerce";
import type { Affiliate } from "@/types/commerce";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Async typeahead picker for affiliates. Symmetric to {@link AdvertiserSelect}.
 *
 * Three value states:
 *   - `undefined`  → "All" (no filter applied) — only when `includeAll`.
 *   - `null`       → "Unassigned" — only when `includeUnassigned`.
 *   - `number`     → A specific affiliate id.
 */
export interface AffiliateSelectProps {
    value: number | null | undefined;
    onChange: (next: number | null | undefined) => void;
    placeholder?: string;
    includeUnassigned?: boolean;
    includeAll?: boolean;
    includeArchived?: boolean;
    disabled?: boolean;
    className?: string;
    "data-testid"?: string;
}

export function AffiliateSelect({
    value,
    onChange,
    placeholder = "Select affiliate…",
    includeUnassigned = true,
    includeAll = true,
    includeArchived = false,
    disabled = false,
    className,
    "data-testid": testId,
}: AffiliateSelectProps): React.ReactNode {
    const [open, setOpen] = React.useState(false);
    const [rawQuery, setRawQuery] = React.useState("");
    const [debouncedQuery, setDebouncedQuery] = React.useState("");

    React.useEffect(() => {
        const handle = window.setTimeout(() => setDebouncedQuery(rawQuery), 200);
        return () => window.clearTimeout(handle);
    }, [rawQuery]);

    const { data: affiliates, isLoading, isError, refetch } = useQuery({
        queryKey: ["affiliates", { includeArchived }],
        queryFn: () => listAffiliates({ includeArchived }),
        staleTime: 30_000,
    });

    const selected = React.useMemo<Affiliate | null>(() => {
        if (typeof value !== "number") return null;
        return affiliates?.find((a) => a.id === value) ?? null;
    }, [affiliates, value]);

    const filtered = React.useMemo(() => {
        if (!affiliates) return [];
        const q = debouncedQuery.trim().toLowerCase();
        if (!q) return affiliates;
        return affiliates.filter(
            (a) =>
                a.name.toLowerCase().includes(q) ||
                a.slug.toLowerCase().includes(q) ||
                (a.contactEmail ?? "").toLowerCase().includes(q) ||
                (a.payoutEmail ?? "").toLowerCase().includes(q),
        );
    }, [affiliates, debouncedQuery]);

    const triggerLabel = (() => {
        if (value === undefined && includeAll) return "All affiliates";
        if (value === null && includeUnassigned) return "Unassigned";
        if (selected) return selected.name;
        if (typeof value === "number") return `#${value}`;
        return placeholder;
    })();

    const triggerIsPlaceholder = value === undefined && !includeAll && !selected;

    const handlePick = (next: number | null | undefined): void => {
        onChange(next);
        setOpen(false);
        setRawQuery("");
    };

    return (
        <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-haspopup="listbox"
                    aria-label="Select affiliate"
                    disabled={disabled}
                    data-testid={testId ?? "affiliate-select"}
                    className={cn(
                        "w-full justify-between font-normal",
                        triggerIsPlaceholder && "text-muted-foreground",
                        className,
                    )}
                >
                    <span className="truncate">{triggerLabel}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
                data-testid={testId ? `${testId}-content` : "affiliate-select-content"}
            >
                <div className="flex items-center gap-2 border-b px-3 py-2">
                    <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <Input
                        autoFocus
                        value={rawQuery}
                        onChange={(e) => setRawQuery(e.target.value)}
                        placeholder="Search by name, slug, or email…"
                        className="h-7 border-0 px-0 shadow-none focus-visible:ring-0"
                        data-testid={testId ? `${testId}-search` : "affiliate-select-search"}
                    />
                </div>
                <div role="listbox" className="max-h-72 overflow-auto py-1">
                    {includeAll && (
                        <OptionRow
                            label="All affiliates"
                            hint="No filter"
                            selected={value === undefined}
                            onSelect={() => handlePick(undefined)}
                            testId={testId ? `${testId}-opt-all` : "affiliate-select-opt-all"}
                        />
                    )}
                    {includeUnassigned && (
                        <OptionRow
                            label="Unassigned"
                            hint="No affiliate"
                            selected={value === null}
                            onSelect={() => handlePick(null)}
                            testId={testId ? `${testId}-opt-unassigned` : "affiliate-select-opt-unassigned"}
                        />
                    )}
                    {(includeAll || includeUnassigned) && <div className="my-1 border-t" />}

                    {isLoading && (
                        <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading affiliates…
                        </div>
                    )}
                    {isError && (
                        <div className="px-3 py-4 text-sm text-destructive">
                            Failed to load affiliates.{" "}
                            <button
                                type="button"
                                className="underline underline-offset-2"
                                onClick={() => void refetch()}
                            >
                                Retry
                            </button>
                        </div>
                    )}
                    {!isLoading && !isError && filtered.length === 0 && (
                        <div className="px-3 py-4 text-sm text-muted-foreground">
                            No affiliates match “{debouncedQuery}”.
                        </div>
                    )}
                    {!isLoading &&
                        !isError &&
                        filtered.map((a) => (
                            <OptionRow
                                key={a.id}
                                label={a.name}
                                hint={`${a.slug}${a.status === "archived" ? " · archived" : ""}`}
                                selected={value === a.id}
                                onSelect={() => handlePick(a.id)}
                                testId={`affiliate-select-opt-${a.id}`}
                            />
                        ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

function OptionRow({
    label,
    hint,
    selected,
    onSelect,
    testId,
}: {
    label: string;
    hint?: string;
    selected: boolean;
    onSelect: () => void;
    testId?: string;
}): React.ReactNode {
    return (
        <button
            type="button"
            role="option"
            aria-selected={selected}
            onClick={onSelect}
            data-testid={testId}
            className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground",
                selected && "bg-accent/50",
            )}
        >
            <Check
                className={cn("h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")}
                aria-hidden="true"
            />
            <span className="flex-1 truncate">{label}</span>
            {hint && (
                <span className="ml-2 shrink-0 text-xs text-muted-foreground">{hint}</span>
            )}
        </button>
    );
}
